import type { ActionArtifact, DrawnCard, Reading, Scenario, VentRitualResult } from "../types";

const W = 1080;
const H = 1440;
const GOLD = "#d5b46d";
const PAPER = "#f6f0e3";
const NAVY = "#07131f";
const INK = "#102332";
const SERIF = '"Noto Serif SC", "Songti SC", serif';
const COMPLIANCE = "内容仅用于自我复盘与决策参考，不替代专业意见";

export interface XhsShareInput {
  scenario: Scenario;
  cards: DrawnCard[];
  report: Reading;
  artifact?: ActionArtifact | null;
}

export interface XhsShareBundle {
  title: string;
  caption: string;
  files: File[];
}

export interface VentShareInput {
  target: string;
  behaviors: string[];
  ritual: VentRitualResult;
  pinPoints?: Array<{ x:number; y:number }>;
}

type CardPainter = (ctx: CanvasRenderingContext2D, index: number, total: number) => void;

export async function buildXhsShareBundle(input: XhsShareInput): Promise<XhsShareBundle> {
  await document.fonts?.ready;
  const painters = buildPainters(input);
  const blobs = await Promise.all(painters.map((paint, index) => renderCard(paint, index, painters.length)));
  const stamp = new Date().toISOString().slice(0, 10);
  return {
    title: `我的${input.scenario}职场复盘`,
    caption: buildCaption(input),
    files: blobs.map((blob, index) => new File([blob], `职场答案之书_${stamp}_${index + 1}.jpg`, { type: "image/jpeg" })),
  };
}

export async function buildVentXhsShareBundle(input: VentShareInput): Promise<XhsShareBundle> {
  await document.fonts?.ready;
  const pages = buildVentPainters(input);
  const blobs = await Promise.all(pages.map((paint, index) => renderCard(paint, index, pages.length)));
  const stamp = new Date().toISOString().slice(0, 10);
  return {
    title: `今日已封印：${input.behaviors[0]}`,
    caption: [
      `【今日职场封印】${input.ritual.ritualTitle}`,
      "",
      `今天碰到：${input.target}`,
      `已扎掉：${input.behaviors.join("、")}`,
      "",
      input.ritual.reliefMessage,
      `气出了，现实里我准备这样处理：${input.ritual.actionSummary}`,
      "",
      "只扎行为，只针对烦心事。虚拟情绪释放，不对应现实人物。",
      "",
      "#职场吐槽 #打工人精神状态 #甲方日常 #职场沟通 #情绪释放练习 #职场答案之书",
    ].join("\n"),
    files: blobs.map((blob, index) => new File([blob], `职场泄压室_${stamp}_${index + 1}.jpg`, { type:"image/jpeg" })),
  };
}

function buildVentPainters({ target, behaviors, ritual, pinPoints }: VentShareInput): CardPainter[] {
  return [
    (ctx, index, total) => {
      darkBackground(ctx); brand(ctx, true); label(ctx, "职场泄压室 · 今日封印", true);
      paragraph(ctx, ritual.ritualTitle, 100, 310, W - 200, 86, { color:PAPER, font:`700 62px ${SERIF}`, maxLines:3, align:"center" });
      drawPaperFigure(ctx, 540, 785, true, pinPoints);
      ctx.fillStyle = "#ff8297"; ctx.font = `600 31px ${SERIF}`; ctx.textAlign = "center"; ctx.fillText(`已封印：${behaviors[0]}`, W / 2, 1130);
      ctx.fillStyle = "#9badb4"; ctx.font = "400 24px sans-serif"; ctx.fillText("情绪释放练习 · 只扎行为，只针对烦心事", W / 2, 1190);
      footer(ctx, index, total, true);
    },
    (ctx, index, total) => {
      lightBackground(ctx); brand(ctx, false); sectionTitle(ctx, "今天扎掉这些破事", target);
      drawPaperFigure(ctx, 270, 790, false, pinPoints);
      behaviors.slice(0, 4).forEach((behavior, itemIndex) => {
        const y = 500 + itemIndex * 155;
        ctx.fillStyle = "#ef4160"; circle(ctx, 545, y - 10, 9); ctx.fill();
        paragraph(ctx, behavior, 590, y, 350, 48, { color:INK, font:`600 33px ${SERIF}`, maxLines:2 });
      });
      ctx.fillStyle = "#9b7832"; ctx.font = `500 30px ${SERIF}`; ctx.textAlign = "center"; ctx.fillText("气到这里为止，主动权从这里回来", W / 2, 1160);
      footer(ctx, index, total, false);
    },
    (ctx, index, total) => {
      lightBackground(ctx); brand(ctx, false); sectionTitle(ctx, "气出了，再把事搞定", "现实反击话术");
      paragraph(ctx, ritual.actionSummary, 120, 430, W - 240, 58, { color:INK, font:`500 35px ${SERIF}`, maxLines:5 });
      ctx.fillStyle = "#e8dcc1"; roundedRect(ctx, 105, 760, 870, 345, 25); ctx.fill();
      ctx.fillStyle = "#a17f36"; ctx.font = "600 24px sans-serif"; ctx.textAlign = "left"; ctx.fillText("可以直接复制", 150, 830);
      paragraph(ctx, `“${ritual.responseScript}”`, 150, 905, 780, 55, { color:INK, font:`500 32px ${SERIF}`, maxLines:4 });
      footer(ctx, index, total, false);
    },
    (ctx, index, total) => {
      darkBackground(ctx); brand(ctx, true);
      ctx.fillStyle = GOLD; ctx.font = `500 30px ${SERIF}`; ctx.textAlign = "center"; ctx.fillText("碰到离谱的人和事", W / 2, 400);
      ctx.fillStyle = PAPER; ctx.font = `700 66px ${SERIF}`; ctx.fillText("先电子扎一下", W / 2, 515); ctx.fillText("再把问题解决掉", W / 2, 610);
      ctx.strokeStyle = "#ef4160"; ctx.lineWidth = 3; roundedRect(ctx, 155, 760, 770, 180, 25); ctx.stroke();
      ctx.fillStyle = "rgba(239,65,96,.12)"; roundedRect(ctx, 155, 760, 770, 180, 25); ctx.fill();
      ctx.fillStyle = "#ffd3da"; ctx.font = `700 48px ${SERIF}`; ctx.fillText("职场泄压室", W / 2, 850);
      ctx.fillStyle = "#aebbc0"; ctx.font = "400 27px sans-serif"; ctx.fillText("职场答案之书", W / 2, 905);
      footer(ctx, index, total, true);
    },
  ];
}

function buildCaption({ scenario, cards, report, artifact }: XhsShareInput) {
  const hooks = [
    "纠结很久以后，我换了一种方式把问题摊开看。",
    "没让牌替我做决定，但它确实提醒了我漏看的地方。",
    "最近有点职场内耗，给自己做了一次三张牌复盘。",
  ];
  const random = new Uint8Array(1); crypto.getRandomValues(random);
  const cardLine = cards.map((card) => `${card.position}：${card.name}${card.orientation}`).join("｜");
  return [
    `【${scenario}】${report.title}`,
    "",
    hooks[random[0] % hooks.length],
    cardLine,
    "",
    `这次最值得记下的提醒：${report.summary}`,
    artifact ? `我最后整理成了「${artifact.title}」，先从能验证的一小步开始。` : "比起预测结果，我更需要的是下一步可以验证的行动。",
    "",
    "图片里放了完整复盘，给同样在职场选择里纠结的人一个参考。",
    "仅供自我复盘与决策参考。",
    "",
    "#职场成长 #职场复盘 #职场选择 #职场行动 #职场答案之书",
  ].join("\n");
}

function buildPainters(input: XhsShareInput): CardPainter[] {
  const { cards, report, artifact, scenario } = input;
  const pages: CardPainter[] = [
    (ctx, index, total) => {
      darkBackground(ctx);
      brand(ctx, true);
      label(ctx, `${scenario} · 三牌复盘`, true);
      paragraph(ctx, report.title, 100, 270, W - 200, 82, { color:PAPER, font:`700 58px ${SERIF}`, maxLines:3, align:"center" });
      cards.forEach((card, cardIndex) => drawMiniCard(ctx, card, 105 + cardIndex * 305, 610));
      paragraph(ctx, report.summary, 135, 1090, W - 270, 52, { color:"#d7dee0", font:`400 31px ${SERIF}`, maxLines:4, align:"center" });
      footer(ctx, index, total, true);
    },
    insightPage("01 · 当前处境", cards[0], report.situation),
    insightPage("02 · 隐藏卡点", cards[1], report.blindSpot),
    insightPage("03 · 下一步行动", cards[2], report.advice),
    (ctx, index, total) => {
      lightBackground(ctx); brand(ctx, false); sectionTitle(ctx, "未来 48 小时", "先做三件小事");
      report.next48Hours.slice(0, 3).forEach((item, actionIndex) => {
        ctx.fillStyle = GOLD; circle(ctx, 125, 450 + actionIndex * 245, 39); ctx.fill();
        ctx.fillStyle = NAVY; ctx.font = "700 28px sans-serif"; ctx.textAlign = "center"; ctx.fillText(String(actionIndex + 1), 125, 460 + actionIndex * 245);
        paragraph(ctx, item, 200, 420 + actionIndex * 245, 740, 53, { color:INK, font:`500 34px ${SERIF}`, maxLines:3 });
        ctx.strokeStyle = "#d8c9a4"; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(200, 570 + actionIndex * 245); ctx.lineTo(930, 570 + actionIndex * 245); ctx.stroke();
      });
      footer(ctx, index, total, false);
    },
  ];
  if (artifact) pages.push(artifactPage(artifact));
  pages.push((ctx, index, total) => {
    darkBackground(ctx); brand(ctx, true);
    ctx.fillStyle = GOLD; ctx.font = `500 28px ${SERIF}`; ctx.textAlign = "center"; ctx.fillText("把模糊的纠结", W / 2, 380);
    ctx.fillStyle = PAPER; ctx.font = `700 68px ${SERIF}`; ctx.fillText("变成下一步行动", W / 2, 485);
    ctx.strokeStyle = GOLD; ctx.lineWidth = 3; roundedRect(ctx, 150, 640, 780, 220, 28); ctx.stroke();
    ctx.fillStyle = "rgba(213,180,109,.10)"; roundedRect(ctx, 150, 640, 780, 220, 28); ctx.fill();
    ctx.fillStyle = "#f1d99e"; ctx.font = `700 48px ${SERIF}`; ctx.fillText("职场答案之书", W / 2, 740);
    ctx.fillStyle = "#aebbc0"; ctx.font = "400 27px sans-serif"; ctx.fillText("亲手抽牌的职场复盘工具", W / 2, 800);
    paragraph(ctx, "答案不在别人嘴里，\n先听见自己的判断。", 170, 1000, 740, 65, { color:PAPER, font:`500 40px ${SERIF}`, align:"center", maxLines:3 });
    footer(ctx, index, total, true);
  });
  return pages.slice(0, 7);
}

function insightPage(title: string, card: DrawnCard, content: string): CardPainter {
  return (ctx, index, total) => {
    lightBackground(ctx); brand(ctx, false); sectionTitle(ctx, title, `${card.name} · ${card.orientation}`);
    ctx.fillStyle = "#ead8a8"; roundedRect(ctx, 110, 395, 860, 160, 22); ctx.fill();
    ctx.fillStyle = INK; ctx.font = `700 32px ${SERIF}`; ctx.textAlign = "left"; ctx.fillText(`${card.symbol}  ${card.archetype}`, 155, 465);
    ctx.fillStyle = "#4f5f67"; ctx.font = `400 26px ${SERIF}`; ctx.fillText(card.brief.slice(0, 34), 155, 515);
    paragraph(ctx, content, 120, 665, W - 240, 64, { color:INK, font:`400 38px ${SERIF}`, maxLines:8 });
    footer(ctx, index, total, false);
  };
}

function artifactPage(artifact: ActionArtifact): CardPainter {
  return (ctx, index, total) => {
    lightBackground(ctx); brand(ctx, false); sectionTitle(ctx, "我的具体行动方案", artifact.title.slice(0, 18));
    paragraph(ctx, artifact.goal, 120, 410, W - 240, 50, { color:"#46575f", font:`400 30px ${SERIF}`, maxLines:3 });
    artifact.steps.slice(0, 4).forEach((step, stepIndex) => {
      const y = 610 + stepIndex * 155;
      ctx.fillStyle = GOLD; ctx.font = "700 32px sans-serif"; ctx.textAlign = "left"; ctx.fillText(String(stepIndex + 1).padStart(2, "0"), 120, y);
      ctx.fillStyle = "#95732e"; ctx.font = "500 22px sans-serif"; ctx.fillText(step.when, 190, y - 30);
      paragraph(ctx, step.action, 190, y + 10, 750, 42, { color:INK, font:`600 29px ${SERIF}`, maxLines:2 });
    });
    footer(ctx, index, total, false);
  };
}

async function renderCard(paint: CardPainter, index: number, total: number) {
  const canvas = document.createElement("canvas"); canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d"); if (!ctx) throw new Error("当前浏览器无法生成分享图片");
  paint(ctx, index, total);
  return await new Promise<Blob>((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("生成分享图失败")), "image/jpeg", .9));
}

function lightBackground(ctx: CanvasRenderingContext2D) {
  const gradient = ctx.createLinearGradient(0, 0, 0, H); gradient.addColorStop(0, "#fbf8f1"); gradient.addColorStop(1, "#efe5d0");
  ctx.fillStyle = gradient; ctx.fillRect(0, 0, W, H); frame(ctx, false);
}
function darkBackground(ctx: CanvasRenderingContext2D) {
  const gradient = ctx.createLinearGradient(0, 0, W, H); gradient.addColorStop(0, "#12364c"); gradient.addColorStop(1, NAVY);
  ctx.fillStyle = gradient; ctx.fillRect(0, 0, W, H); frame(ctx, true);
}
function frame(ctx: CanvasRenderingContext2D, dark: boolean) {
  ctx.strokeStyle = dark ? "rgba(213,180,109,.7)" : GOLD; ctx.lineWidth = 3; ctx.strokeRect(45, 45, W - 90, H - 90);
  ctx.strokeStyle = "rgba(213,180,109,.25)"; ctx.lineWidth = 1; ctx.strokeRect(62, 62, W - 124, H - 124);
}
function brand(ctx: CanvasRenderingContext2D, dark: boolean) {
  ctx.textAlign = "center"; ctx.fillStyle = dark ? "#f1d99e" : INK; ctx.font = `600 29px ${SERIF}`; ctx.fillText("✦  职 场 答 案 之 书  ✦", W / 2, 125);
}
function label(ctx: CanvasRenderingContext2D, value: string, dark: boolean) {
  ctx.fillStyle = dark ? "#d5b46d" : "#95732e"; ctx.font = "600 24px sans-serif"; ctx.textAlign = "center"; ctx.fillText(value, W / 2, 205);
}
function sectionTitle(ctx: CanvasRenderingContext2D, value: string, badge: string) {
  ctx.textAlign = "left"; ctx.fillStyle = INK; ctx.font = `700 54px ${SERIF}`; ctx.fillText(value, 110, 265);
  ctx.fillStyle = "#9c7b35"; ctx.font = "500 24px sans-serif"; ctx.fillText(badge, 112, 325);
  ctx.strokeStyle = GOLD; ctx.lineWidth = 5; ctx.beginPath(); ctx.moveTo(110, 350); ctx.lineTo(220, 350); ctx.stroke();
}
function footer(ctx: CanvasRenderingContext2D, index: number, total: number, dark: boolean) {
  ctx.textAlign = "center"; ctx.fillStyle = dark ? "#869aa3" : "#7c8b8f"; ctx.font = "400 21px sans-serif"; ctx.fillText(COMPLIANCE, W / 2, H - 105);
  ctx.fillStyle = dark ? "#b59655" : "#aa8b4b"; ctx.font = "400 19px sans-serif"; ctx.fillText(`职场答案之书  ·  ${index + 1}/${total}`, W / 2, H - 68);
}
function drawMiniCard(ctx: CanvasRenderingContext2D, card: DrawnCard, x: number, y: number) {
  ctx.fillStyle = "#e7d39d"; roundedRect(ctx, x, y, 260, 350, 14); ctx.fill();
  ctx.strokeStyle = "#8f7030"; ctx.lineWidth = 3; roundedRect(ctx, x + 12, y + 12, 236, 326, 8); ctx.stroke();
  ctx.fillStyle = INK; ctx.textAlign = "center"; ctx.font = `700 70px ${SERIF}`; ctx.fillText(card.symbol, x + 130, y + 150);
  ctx.font = `700 30px ${SERIF}`; ctx.fillText(card.name, x + 130, y + 235);
  ctx.font = "500 20px sans-serif"; ctx.fillText(card.orientation, x + 130, y + 278);
  ctx.fillStyle = "#917231"; ctx.font = "500 19px sans-serif"; ctx.fillText(card.position, x + 130, y + 390);
}
function drawPaperFigure(ctx: CanvasRenderingContext2D, x:number, y:number, dark:boolean, pinPoints?:Array<{x:number;y:number}>) {
  ctx.save(); ctx.translate(x, y); ctx.strokeStyle = dark ? "#e7d39d" : "#17384c"; ctx.fillStyle = dark ? "rgba(231,211,157,.1)" : "#ead8a8"; ctx.lineWidth = 8;
  circle(ctx, 0, -210, 74); ctx.fill(); ctx.stroke();
  roundedRect(ctx, -95, -120, 190, 250, 55); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-75, -75); ctx.lineTo(-175, 45); ctx.moveTo(75, -75); ctx.lineTo(175, 45); ctx.moveTo(-48, 120); ctx.lineTo(-95, 245); ctx.moveTo(48, 120); ctx.lineTo(95, 245); ctx.stroke();
  const sharePins = pinPoints?.length ? pinPoints.slice(0,7).map((point)=>[-175+(point.x/100)*350,-284+(point.y/100)*529]) : [[-35,-35],[45,35],[-5,75]];
  sharePins.forEach(([pinX,pinY]) => { ctx.strokeStyle="#ef4160";ctx.lineWidth=6;ctx.beginPath();ctx.moveTo(pinX-42,pinY-42);ctx.lineTo(pinX+42,pinY+42);ctx.stroke();ctx.fillStyle="#ef4160";circle(ctx,pinX-48,pinY-48,13);ctx.fill(); });
  ctx.restore();
}
function paragraph(ctx: CanvasRenderingContext2D, value: string, x: number, y: number, maxWidth: number, lineHeight: number, options: { color:string;font:string;maxLines:number;align?:CanvasTextAlign }) {
  ctx.fillStyle = options.color; ctx.font = options.font; ctx.textAlign = options.align || "left";
  const lines: string[] = [];
  for (const block of String(value).split("\n")) {
    let line = "";
    for (const char of block) { if (line && ctx.measureText(line + char).width > maxWidth) { lines.push(line); line = char; } else line += char; }
    if (line) lines.push(line);
  }
  lines.slice(0, options.maxLines).forEach((line, index) => ctx.fillText(line, x + (options.align === "center" ? maxWidth / 2 : 0), y + index * lineHeight));
}
function roundedRect(ctx: CanvasRenderingContext2D, x:number, y:number, width:number, height:number, radius:number) {
  ctx.beginPath(); ctx.moveTo(x + radius, y); ctx.arcTo(x + width, y, x + width, y + height, radius); ctx.arcTo(x + width, y + height, x, y + height, radius); ctx.arcTo(x, y + height, x, y, radius); ctx.arcTo(x, y, x + width, y, radius); ctx.closePath();
}
function circle(ctx: CanvasRenderingContext2D, x:number, y:number, radius:number) { ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2); ctx.closePath(); }
